import React, { useCallback, useEffect, useState } from 'react';
import { ReminderPolicyApi } from '../../services/api/ReminderPolicyApi';

/** Human-friendly labels for known policy categories. */
const CATEGORY_LABELS = {
  milestone: 'Milestones',
  priority_high: 'High priority assignments',
  priority_normal: 'Normal priority assignments',
  priority_low_short: 'Low priority (short title)',
  class_event: 'Class / event reminders',
  overdue: 'Overdue nudges',
  global_daily_cap: 'Daily reminder cap (per user)',
  digest_hour_local: 'Digest hour (local, 0–23)',
};

/** Categories that hold a single scalar rather than a CSV of lead times. */
const SCALAR_CATEGORIES = new Set(['global_daily_cap', 'digest_hour_local']);

function describeCategory(category) {
  return CATEGORY_LABELS[category] || category;
}

/**
 * Validate the CSV value for a category. Returns an error string or null.
 * - Scalar categories must be a single non-negative integer.
 * - Lead-time categories must be comma-separated integers (negatives allowed
 *   for overdue nudges).
 */
function validateCsv(category, csv) {
  const raw = (csv || '').trim();
  if (raw === '') {
    return SCALAR_CATEGORIES.has(category) ? 'A value is required.' : null;
  }
  const parts = raw.split(',').map((p) => p.trim());
  if (SCALAR_CATEGORIES.has(category)) {
    if (parts.length !== 1) return 'Enter a single number.';
    const n = Number(parts[0]);
    if (!Number.isInteger(n) || n < 0) return 'Must be a non-negative integer.';
    if (category === 'digest_hour_local' && n > 23) return 'Hour must be between 0 and 23.';
    return null;
  }
  for (const p of parts) {
    const n = Number(p);
    if (!Number.isInteger(n)) return 'All values must be whole numbers.';
    if (Math.abs(n) > 100000) return 'Values are out of a sensible range.';
  }
  return null;
}

export default function ReminderPolicyPage() {
  const [policies, setPolicies] = useState([]);
  const [drafts, setDrafts] = useState({}); // category -> { leadMinutesCsv, enabled, description }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingCategory, setSavingCategory] = useState(null);
  const [resetting, setResetting] = useState(false);

  const hydrate = useCallback((rows) => {
    const list = Array.isArray(rows) ? rows : [];
    setPolicies(list);
    const next = {};
    for (const p of list) {
      next[p.category] = {
        leadMinutesCsv: p.leadMinutesCsv ?? '',
        enabled: !!p.enabled,
        description: p.description ?? '',
      };
    }
    setDrafts(next);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await ReminderPolicyApi.list();
      hydrate(rows);
    } catch (err) {
      setError(err.message || 'Failed to load reminder policies.');
    } finally {
      setLoading(false);
    }
  }, [hydrate]);

  useEffect(() => {
    load();
  }, [load]);

  const updateDraft = (category, patch) => {
    setDrafts((prev) => ({ ...prev, [category]: { ...prev[category], ...patch } }));
  };

  const isDirty = (category) => {
    const original = policies.find((p) => p.category === category);
    const draft = drafts[category];
    if (!original || !draft) return false;
    return (
      (original.leadMinutesCsv ?? '') !== draft.leadMinutesCsv ||
      !!original.enabled !== draft.enabled ||
      (original.description ?? '') !== draft.description
    );
  };

  const handleSave = async (category) => {
    const draft = drafts[category];
    const validationError = validateCsv(category, draft.leadMinutesCsv);
    if (validationError) {
      setError(`${describeCategory(category)}: ${validationError}`);
      return;
    }
    setSavingCategory(category);
    setError(null);
    try {
      const updated = await ReminderPolicyApi.update(category, {
        leadMinutesCsv: draft.leadMinutesCsv.trim(),
        enabled: draft.enabled,
        description: draft.description,
      });
      setPolicies((prev) => prev.map((p) => (p.category === category ? updated : p)));
      updateDraft(category, {
        leadMinutesCsv: updated.leadMinutesCsv ?? '',
        enabled: !!updated.enabled,
        description: updated.description ?? '',
      });
    } catch (err) {
      setError(err.message || 'Failed to save policy.');
    } finally {
      setSavingCategory(null);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all reminder policies to their default values?')) return;
    setResetting(true);
    setError(null);
    try {
      const rows = await ReminderPolicyApi.reset();
      hydrate(rows);
    } catch (err) {
      setError(err.message || 'Failed to reset policies.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Reminder Policies</h1>
          <p style={styles.subtitle}>
            Control how many reminders each assignment category generates, the
            per-user daily cap, and the local digest hour. Lead times are minutes
            before the due date (negative values fire after it).
          </p>
        </div>
        <button
          type="button"
          style={styles.resetBtn}
          onClick={handleReset}
          disabled={resetting || loading}
        >
          {resetting ? 'Resetting…' : 'Reset to defaults'}
        </button>
      </div>

      {error ? <div style={styles.error}>{error}</div> : null}

      {loading ? (
        <div style={styles.loading}>Loading…</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>{'Lead minutes / value'}</th>
              <th style={styles.th}>Enabled</th>
              <th style={styles.th}>Description</th>
              <th style={styles.th} />
            </tr>
          </thead>
          <tbody>
            {policies.map((p) => {
              const draft = drafts[p.category] || {};
              const dirty = isDirty(p.category);
              return (
                <tr key={p.category} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.categoryLabel}>{describeCategory(p.category)}</div>
                    <div style={styles.categoryKey}>{p.category}</div>
                  </td>
                  <td style={styles.td}>
                    <input
                      type="text"
                      value={draft.leadMinutesCsv ?? ''}
                      onChange={(e) => updateDraft(p.category, { leadMinutesCsv: e.target.value })}
                      placeholder={SCALAR_CATEGORIES.has(p.category) ? 'e.g. 4' : 'e.g. 1440,60'}
                      style={styles.input}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="checkbox"
                      checked={!!draft.enabled}
                      onChange={(e) => updateDraft(p.category, { enabled: e.target.checked })}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="text"
                      value={draft.description ?? ''}
                      onChange={(e) => updateDraft(p.category, { description: e.target.value })}
                      style={{ ...styles.input, width: '100%' }}
                    />
                  </td>
                  <td style={styles.td}>
                    <button
                      type="button"
                      style={{ ...styles.saveBtn, ...(dirty ? {} : styles.saveBtnDisabled) }}
                      onClick={() => handleSave(p.category)}
                      disabled={!dirty || savingCategory === p.category}
                    >
                      {savingCategory === p.category ? 'Saving…' : 'Save'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '24px', maxWidth: 1100, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 },
  title: { margin: 0, fontSize: 24, fontWeight: 700 },
  subtitle: { color: '#6b7280', fontSize: 14, maxWidth: 720, marginTop: 6 },
  resetBtn: {
    padding: '8px 14px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    background: '#fff',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  error: {
    marginTop: 16,
    padding: '10px 14px',
    background: '#fef2f2',
    color: '#b91c1c',
    border: '1px solid #fecaca',
    borderRadius: 8,
    fontSize: 14,
  },
  loading: { marginTop: 24, color: '#6b7280' },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: 20 },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: '2px solid #e5e7eb',
    fontSize: 13,
    color: '#374151',
  },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '12px', verticalAlign: 'top' },
  categoryLabel: { fontWeight: 600, fontSize: 14 },
  categoryKey: { color: '#9ca3af', fontSize: 12, fontFamily: 'monospace' },
  input: {
    padding: '6px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 14,
    minWidth: 120,
  },
  saveBtn: {
    padding: '6px 14px',
    border: 'none',
    borderRadius: 6,
    background: '#3b82f6',
    color: '#fff',
    cursor: 'pointer',
    fontSize: 14,
  },
  saveBtnDisabled: { background: '#cbd5e1', cursor: 'default' },
};
